import React, { useEffect, useState } from 'react';
import { Badge, Col, Container, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import LockResetIcon from 'mdi-react/LockResetIcon';
import BlockHelperIcon from 'mdi-react/BlockHelperIcon';

import { getLeadApiResourcesFetchingAction } from '../../../redux/actions/leadApiResourcesActions';
import DataReactTable from '../../Tables/DataTable/components/DataReactTable';
import { columns } from './components/LeadApiKeysDataColumns';
import ConfirmModal from '../../../shared/components/customComponents/CustomConfirmModal/ConfirmModal';
import CustomModal from '../../../shared/components/customComponents/CustomModal/CustomModal';
import ApiKeyRevealModal from './components/ApiKeyRevealModal';
import { postReissueLeadApiResource, postRevokeLeadApiResource } from '../../../utils/api';

const Integrations = () => {
  const { leadApiResources, errors, loading } = useSelector(state => state.leadApiResources);
  const authUser = useSelector(state => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [refreshCounter, setRefreshCounter] = useState(0);
  const [revokeId, setRevokeId] = useState(null);
  const [confirmRevokeModal, setConfirmRevokeModal] = useState(false);
  const [reissueId, setReissueId] = useState(null);
  const [confirmReissueModal, setConfirmReissueModal] = useState(false);
  const [revealModal, setRevealModal] = useState(false);
  const [reissuedKey, setReissuedKey] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorModal, setErrorModal] = useState(false);

  useEffect(() => {
    dispatch(getLeadApiResourcesFetchingAction());
  }, [dispatch, refreshCounter]);

  const handleRevoke = () => {
    postRevokeLeadApiResource(revokeId, authUser.accessToken).then(res => {
      if (res.data.success) {
        setRefreshCounter(refreshCounter + 1);
      } else {
        setErrorMessage(res.data.errors[0].message);
        setErrorModal(true);
      }
    });
  };

  const handleReissue = () => {
    postReissueLeadApiResource(reissueId, authUser.accessToken).then(res => {
      if (res.data.success) {
        setReissuedKey(res.data.data.api_key);
        setRevealModal(true);
        setRefreshCounter(refreshCounter + 1);
      } else {
        setErrorMessage(res.data.errors[0].message);
        setErrorModal(true);
      }
    });
  };

  return (
    <Container>
      <Row>
        <Col className="col-12 d-flex justify-content-between align-items-center">
          <h3 className="page-title mb-0">Integrations</h3>
          <button className="btn btn-success" onClick={() => navigate('/settings/integrations/add')}>
            Add a new resource
          </button>
        </Col>
        <Col className="col-12">
          <p className="text-muted">
            Resources (domains/sites) allowed to create leads via the public Leads API (<code>POST /api/v1/leads</code>).
            Each resource authenticates with its own key, sent in the <code>X-Api-Key</code> header. A key is shown in
            full only once — right after it is generated or reissued.
          </p>
        </Col>
        <Col className="col-12">
          <DataReactTable
            pagination={false}
            sortTable
            search={false}
            loading={loading}
            emptyStateMessage="No resources have been added yet."
            reactTableData={{
              tableHeaderData: columns,
              tableRowsData: leadApiResources?.map((c) => {
                return {
                  ...c,
                  id: `${c.id}`,
                  status: c.is_active ? (
                    <Badge color="success">Active</Badge>
                  ) : (
                    <Badge color="danger">Revoked</Badge>
                  ),
                  request_count: c.request_count ?? 0,
                  created_by: c.created_by || '—',
                  last_used_at: c.last_used_at || 'Never',
                  actions: (
                    <>
                      <ReactTooltip id="reissueIcon" type="dark">
                        <span style={{ color: 'white' }}>Reissue key</span>
                      </ReactTooltip>
                      <button
                        data-tip
                        data-for="reissueIcon"
                        style={{ border: 0, background: 'transparent', padding: '5px 0 5px 5px' }}
                        onClick={() => {
                          setReissueId(c.id);
                          setConfirmReissueModal(true);
                        }}
                      >
                        <LockResetIcon size={18} color="#b1c3c8" />
                      </button>
                      {c.is_active ? (
                        <>
                          <ReactTooltip id="revokeIcon" type="dark">
                            <span style={{ color: 'white' }}>Revoke key</span>
                          </ReactTooltip>
                          <button
                            data-tip
                            data-for="revokeIcon"
                            style={{ border: 0, background: 'transparent', padding: '5px 0 5px 5px' }}
                            onClick={() => {
                              setRevokeId(c.id);
                              setConfirmRevokeModal(true);
                            }}
                          >
                            <BlockHelperIcon size={18} color="#b1c3c8" />
                          </button>
                        </>
                      ) : null}
                    </>
                  ),
                };
              }),
            }}
          />
          {errors ? <div>{errors}</div> : null}
        </Col>
      </Row>

      <ConfirmModal
        color="danger"
        message={`Revoke the API key for resource #${revokeId}? It will stop working immediately — this cannot be undone, only reissuing a new key will re-enable the resource.`}
        toggle={() => setConfirmRevokeModal(!confirmRevokeModal)}
        modal={confirmRevokeModal}
        onNext={handleRevoke}
      />
      <ConfirmModal
        color="primary"
        message={`Reissue the API key for resource #${reissueId}? The old key will stop working immediately and a new one will be generated (this also re-activates a revoked resource).`}
        toggle={() => setConfirmReissueModal(!confirmReissueModal)}
        modal={confirmReissueModal}
        onNext={handleReissue}
      />
      <ApiKeyRevealModal
        modal={revealModal}
        toggle={() => setRevealModal(!revealModal)}
        apiKey={reissuedKey}
        title="New API key generated"
      />
      <CustomModal
        successModal={errorModal}
        toggleCloseModal={() => setErrorModal(!errorModal)}
        textModal={errorMessage}
        color="danger"
      />
    </Container>
  );
};

export default Integrations;
